<template>
  <div class="maintenance-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <div class="page-title">
            <div class="title-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            </div>
            <h1>Complaint & Maintenance Management</h1>
          </div>
        </div>
        <div class="header-actions">
          <button class="btn btn-primary" @click="openCreateModal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Create Complaint</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-section">
      <div class="stat-card">
        <div class="stat-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 12h6M9 16h6M17 21H7a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z"/>
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ totalComplaints }}</h3>
          <p>Total Complaint</p>
          <span class="stat-change positive">+5.4%</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon active">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ totalInProgress }}</h3>
          <p>In Progress</p>
          <span class="stat-change negative">-5.4%</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ totalPending }}</h3>
          <p>Pending</p>
          <span class="stat-change positive">+5.4%</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ avgResolvedTime }} Days</h3>
          <p>Avg. Resolved Time</p>
          <span class="stat-change positive">+5.4%</span>
        </div>
      </div>
    </div>

    <!-- Filter and Note Section -->
    <div class="filter-section">
      <div class="filter-controls">
        <select v-model="selectedPriority" class="filter-select" @change="handleFilterChange">
          <option value="">Priority Type</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <select v-model="selectedStatus" class="filter-select" @change="handleFilterChange">
          <option value="">Status Type</option>
          <option value="PENDING">Pending</option>
          <option value="INPROGRESS">In Progress</option>
          <option value="CLOSED">Done</option>
        </select>
      </div>
      <!-- Edit Restriction Note -->
      <div class="edit-note-section">
        <div class="edit-note">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
          <span>Complain cannot be edited if it's raised by Guest or if status is Done</span>
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div class="table-section">

      <div class="table-container">
        <div class="table-loading-overlay" v-if="isLoading">
          <div class="loading-content">
            <div class="spinner"></div>
            <p class="loading-text">Loading complaints...</p>
          </div>
        </div>
        
        <div class="table-wrapper">
          <table class="complaint-table">
            <thead v-if="filteredComplaints.length > 0 && !isLoading">
              <tr>
                <th>Guest Name</th>
                <th>Room Type</th>
                <th>Room Number</th>
                <th>Complaint Name</th>
                <th>Complaint Date</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Resolved Date</th>
                <th>Raised By</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="complaint in filteredComplaints" :key="complaint.complainId" class="table-row">
                <td>{{ complaint.guestName || '--' }}</td>
                <td>{{ formatRoomType(complaint.roomType) || '--' }}</td>
                <td>{{ complaint.roomNumber || '--' }}</td>
                <td>{{ complaint.complainName || '--' }}</td>
                <td>{{ formatDate(complaint.complainDate) }}</td>
                <td>
                  <span :class="['priority-badge', getPriorityClass(complaint.priority)]">
                    <span class="priority-dot"></span>
                    {{ getPriorityLabel(complaint.priority) }}
                  </span>
                </td>
                <td>
                  <span :class="['status-badge', getStatusClass(complaint.status)]">
                    {{ getStatusLabel(complaint.status) }}
                  </span>
                </td>
                <td>{{ complaint.resolvedDate ? formatDate(complaint.resolvedDate) : '--' }}</td>
                <td>{{ getRaisedByLabel(complaint.raisedBy) || '--' }}</td>
                <td>
                  <div class="action-buttons">
                    <button class="action-btn action-view" @click="openComplaintDetails(complaint)" title="View Details">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                    <button 
                      v-if="complaint.status !== 'CLOSED' && complaint.raisedBy !== 'GUEST'"
                      class="action-btn action-edit" 
                      @click="openEditModal(complaint)" 
                      title="Edit Complaint"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredComplaints.length === 0 && !isLoading" class="empty-row">
                <td colspan="10" class="empty-message">
                  <div class="empty-state">
                    <img src="@/assets/guest-maint-staff.svg" alt="No complaints illustration" class="empty-illustration-img" />
                    <p>No complaints found</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrapper" v-if="totalPages > 1">
        <button 
          class="pagination-btn" 
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <button 
          v-for="page in visiblePages" 
          :key="page"
          class="pagination-btn"
          :class="{ active: page === currentPage }"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>
        <button 
          class="pagination-btn" 
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Complaint Details Modal -->
    <div v-if="showDetailsModal" class="modal-overlay" @click.self="closeComplaintDetails">
      <div class="modal-content">
        <div class="modal-header">
          <div class="header-background"></div>
          <div class="modal-header-content">
            <h2>Complaint Details</h2>
          </div>
          <button class="close-btn" @click="closeComplaintDetails">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <!-- Complaint Title -->
          <div class="complaint-title-section">
            <h3>{{ selectedComplaint?.complainName }}</h3>
            <span :class="['priority-tag', getPriorityClass(selectedComplaint?.priority)]">
              {{ getPriorityLabel(selectedComplaint?.priority) }}
            </span>
          </div>

          <!-- Tenant Details -->
          <div class="details-section">
            <h4>Tenant Details</h4>
            <div class="details-grid">
              <div class="detail-item">
                <label>Guest Name:</label>
                <span>{{ selectedComplaint?.guestName || '--' }}</span>
              </div>
              <div class="detail-item">
                <label>Room Number:</label>
                <span>{{ selectedComplaint?.roomNumber || '--' }}</span>
              </div>
              <div class="detail-item">
                <label>Room Type:</label>
                <span>{{ formatRoomType(selectedComplaint?.roomType) || '--' }}</span>
              </div>
              <div class="detail-item">
                <label>Phone Number:</label>
                <span>{{ selectedComplaint?.mobileno ? `+977 ${selectedComplaint.mobileno}` : '--' }}</span>
              </div>
            </div>
          </div>

          <!-- Issue Description -->
          <div class="details-section">
            <h4>Issue Description</h4>
            <div class="description-content">
              <p class="reported-date">Reported on: {{ formatDate(selectedComplaint?.complainDate) }}</p>
              <p class="description-text">{{ selectedComplaint?.complainDescription || 'No description provided' }}</p>
              
              <!-- Attachments -->
              <div v-if="selectedComplaint?.complainImages && selectedComplaint.complainImages.length > 0" class="attachments-section">
                <div class="attachments-grid">
                  <div 
                    v-for="(imageUrl, index) in selectedComplaint.complainImages" 
                    :key="index"
                    class="attachment-item"
                    @click="openImageModal(imageUrl)"
                  >
                    <img :src="imageUrl" :alt="`Attachment ${index + 1}`" />
                    <div v-if="isVideoUrl(imageUrl)" class="play-overlay">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                      <span>0:10</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Assign Staff -->
          <div class="details-section">
            <h4>Assign Staff</h4>
            <div class="staff-input-wrapper">
              <input 
                type="text"
                v-model="assignedStaff" 
                class="staff-input"
                placeholder="Enter staff name"
                :disabled="selectedComplaint?.status === 'CLOSED'"
              />
              <input 
                type="tel"
                v-model="staffMobileNumber" 
                class="staff-input"
                placeholder="Enter staff mobile number"
                :disabled="selectedComplaint?.status === 'CLOSED'"
              />
            </div>
          </div>

        </div>

        <!-- Action Buttons - Sticky -->
        <div class="modal-actions">
          <button 
            class="btn-action btn-in-progress"
            @click="updateComplaintStatus('INPROGRESS')"
            :disabled="selectedComplaint?.status === 'CLOSED' || isUpdating"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
            </svg>
            Mark as In Progress
          </button>
          <button 
            class="btn-action btn-resolved"
            @click="updateComplaintStatus('CLOSED')"
            :disabled="selectedComplaint?.status === 'CLOSED' || isUpdating"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Mark as Resolved
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Complaint Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-content create-modal">
        <div class="modal-header">
          <div class="header-background"></div>
          <div class="modal-header-content">
            <h2>Edit Complaint</h2>
          </div>
          <button class="close-btn" @click="closeEditModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="updateComplaint" class="create-complaint-form">
            <!-- Guest ID -->
            <div class="form-group">
              <label for="editGuestId">Guest ID <span class="required">*</span></label>
              <input 
                id="editGuestId"
                type="text" 
                v-model="editComplaint.guestId" 
                class="form-input"
                placeholder="Enter guest ID"
                required
                disabled
              />
            </div>

            <!-- Complaint Name -->
            <div class="form-group">
              <label for="editComplainName">Complaint Name <span class="required">*</span></label>
              <input 
                id="editComplainName"
                type="text" 
                v-model="editComplaint.complainName" 
                class="form-input"
                placeholder="e.g., Water leakage in bathroom"
                required
              />
            </div>

            <!-- Complaint Description -->
            <div class="form-group">
              <label for="editComplainDescription">Description <span class="required">*</span></label>
              <textarea 
                id="editComplainDescription"
                v-model="editComplaint.complainDescription" 
                class="form-textarea"
                placeholder="Describe the issue in detail..."
                rows="4"
                required
              ></textarea>
            </div>

            <!-- Priority -->
            <div class="form-group">
              <label for="editPriority">Priority <span class="required">*</span></label>
              <select 
                id="editPriority"
                v-model="editComplaint.priority" 
                class="form-select"
                required
              >
                <option value="">Select Priority</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>

            <!-- Image Upload -->
            <div class="form-group">
              <label>Complaint Images</label>
              <div class="image-upload-section">
                <div class="image-upload-area" @click="triggerEditImageUpload">
                  <input 
                    ref="editImageInput"
                    type="file" 
                    multiple 
                    accept="image/*" 
                    @change="handleEditImageUpload"
                    style="display: none"
                  />
                  <div class="upload-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <p>Click to upload images</p>
                    <span class="upload-hint">JPEG, PNG, GIF, WebP (Max 5MB each)</span>
                  </div>
                </div>

                <!-- Image Previews -->
                <div v-if="editImagePreviews.length > 0" class="image-previews">
                  <div 
                    v-for="(preview, index) in editImagePreviews" 
                    :key="index"
                    class="image-preview-item"
                  >
                    <img :src="preview.url" :alt="`Preview ${index + 1}`" />
                    <div v-if="preview.uploading" class="upload-loading-overlay">
                      <div class="spinner-container">
                        <div class="modern-spinner">
                          <div class="spinner-ring"></div>
                          <div class="spinner-ring"></div>
                          <div class="spinner-ring"></div>
                        </div>
                        <p class="uploading-text">Uploading...</p>
                      </div>
                    </div>
                    <button 
                      type="button"
                      class="remove-image-btn" 
                      @click="removeEditImage(index)"
                      :disabled="preview.uploading"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Form Actions - Sticky -->
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" @click="closeEditModal" :disabled="isUpdatingComplaint">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="updateComplaint" :disabled="isUpdatingComplaint">
            <span v-if="isUpdatingComplaint">Updating...</span>
            <span v-else>Update Complaint</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showImageModal" class="image-modal-overlay" @click.self="closeImageModal">
      <div class="image-modal-content">
        <button class="image-close-btn" @click="closeImageModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <img :src="selectedImageUrl" alt="Complaint attachment" />
      </div>
    </div>

    <!-- Create Complaint Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal-content create-modal">
        <div class="modal-header">
          <div class="header-background"></div>
          <div class="modal-header-content">
            <h2>Create Complaint</h2>
          </div>
          <button class="close-btn" @click="closeCreateModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="createComplaint" class="create-complaint-form">
            <!-- Guest -->
            <div class="form-group">
              <label for="guestId">Guest <span class="required">*</span></label>
              <select
                id="guestId"
                v-model="newComplaint.guestId"
                class="form-select"
                required
              >
                <option value="">Select Guest</option>
                <option v-if="isFetchingComplaintGuests" value="" disabled>Loading guests...</option>
                <option v-else-if="complaintGuests.length === 0" value="" disabled>No active guests found</option>
                <option
                  v-for="guest in complaintGuests"
                  :key="guest.guestId"
                  :value="guest.guestId"
                >
                  {{ getComplaintGuestLabel(guest) }}
                </option>
              </select>
            </div>

            <!-- Complaint Name -->
            <div class="form-group">
              <label for="complainName">Complaint Name <span class="required">*</span></label>
              <input 
                id="complainName"
                type="text" 
                v-model="newComplaint.complainName" 
                class="form-input"
                placeholder="e.g., Water leakage in bathroom"
                required
              />
            </div>

            <!-- Complaint Description -->
            <div class="form-group">
              <label for="complainDescription">Description <span class="required">*</span></label>
              <textarea 
                id="complainDescription"
                v-model="newComplaint.complainDescription" 
                class="form-textarea"
                placeholder="Describe the issue in detail..."
                rows="4"
                required
              ></textarea>
            </div>

            <!-- Priority -->
            <div class="form-group">
              <label for="priority">Priority <span class="required">*</span></label>
              <select 
                id="priority"
                v-model="newComplaint.priority" 
                class="form-select"
                required
              >
                <option value="">Select Priority</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>

            <!-- Image Upload -->
            <div class="form-group">
              <label>Complaint Images</label>
              <div class="image-upload-section">
                <div class="image-upload-area" @click="triggerImageUpload">
                  <input 
                    ref="imageInput"
                    type="file" 
                    multiple 
                    accept="image/*" 
                    @change="handleImageUpload"
                    style="display: none"
                  />
                  <div class="upload-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <p>Click to upload images</p>
                    <span class="upload-hint">JPEG, PNG, GIF, WebP (Max 5MB each)</span>
                  </div>
                </div>

                <!-- Image Previews -->
                <div v-if="complaintImagePreviews.length > 0" class="image-previews">
                  <div 
                    v-for="(preview, index) in complaintImagePreviews" 
                    :key="index"
                    class="image-preview-item"
                  >
                    <img :src="preview.url" :alt="`Preview ${index + 1}`" />
                    <div v-if="preview.uploading" class="upload-loading-overlay">
                      <div class="spinner-container">
                        <div class="modern-spinner">
                          <div class="spinner-ring"></div>
                          <div class="spinner-ring"></div>
                          <div class="spinner-ring"></div>
                        </div>
                        <p class="uploading-text">Uploading...</p>
                      </div>
                    </div>
                    <button 
                      type="button"
                      class="remove-image-btn" 
                      @click="removeImage(index)"
                      :disabled="preview.uploading"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Form Actions - Sticky -->
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" @click="closeCreateModal" :disabled="isCreating">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="createComplaint" :disabled="isCreating">
            <span v-if="isCreating">Creating...</span>
            <span v-else>Create Complaint</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <transition-group name="toast" tag="div">
        <div 
          v-for="toast in toasts" 
          :key="toast.id" 
          class="toast"
          :class="[`toast-${toast.type}`]"
        >
          <div class="toast-icon">
            <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
            <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-message">{{ toast.message }}</p>
          </div>
          <button class="toast-close" @click="removeToast(toast.id)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import { readSessionToken } from '@/utilities/sessionTokenStorage';
import { getComplaintImageUrl } from '@/config/env';

export default {
  name: 'MaintenanceView',
  props: {
    objectData: {
      type: Object,
      default: () => ({})
    },
    objectSpaces: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      complaints: [],
      filteredComplaints: [],
      isLoading: false,
      searchQuery: '',
      selectedPriority: '',
      selectedStatus: '',
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      totalComplaints: 0,
      totalInProgress: 0,
      totalPending: 0,
      avgResolvedTime: 0,
      showDetailsModal: false,
      selectedComplaint: null,
      assignedStaff: '',
      staffMobileNumber: '',
      isUpdating: false,
      showImageModal: false,
      selectedImageUrl: '',
      toasts: [],
      showCreateModal: false,
      isCreating: false,
      isFetchingComplaintGuests: false,
      complaintGuests: [],
      newComplaint: {
        guestId: '',
        complainName: '',
        complainDescription: '',
        priority: '',
        complainImages: []
      },
      complaintImagePreviews: [],
      maxFileSize: 5 * 1024 * 1024, // 5MB
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      showEditModal: false,
      isUpdatingComplaint: false,
      editComplaint: {
        complainId: '',
        guestId: '',
        complainName: '',
        complainDescription: '',
        priority: '',
        complainImages: []
      },
      editImagePreviews: [],
      editingComplaint: null
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const maxVisible = 4;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  methods: {
    // Helper method to normalize complainImages to always be an array
    normalizeComplainImages(complaint) {
      if (!complaint) return complaint;
      
      // If complainImages is a string, convert it to an array
      if (typeof complaint.complainImages === 'string' && complaint.complainImages.trim() !== '') {
        complaint.complainImages = [complaint.complainImages];
      } else if (!Array.isArray(complaint.complainImages)) {
        complaint.complainImages = [];
      }
      
      return complaint;
    },
    getComplaintGuestLabel(guest) {
      const firstName = typeof guest?.firstName === 'string' ? guest.firstName.trim() : '';
      const roomNumber = guest?.roomNumber ? String(guest.roomNumber).trim() : '--';
      return `${firstName || 'guest'} (${roomNumber})`;
    },
    async fetchComplaintGuests() {
      const objectId = this.$route.params.id;
      if (!objectId) {
        this.complaintGuests = [];
        return;
      }

      this.isFetchingComplaintGuests = true;
      try {
        const pageSize = 50;
        let currentPage = 1;
        let totalCount = 0;
        const guests = [];

        do {
          const response = await apiService.getGuests({
            objectId,
            page: currentPage,
            pagesize: pageSize,
            status: 'ACTIVE'
          });

          const pageData = Array.isArray(response?.data?.data) ? response.data.data : [];
          totalCount = Number(response?.data?.totalCount) || 0;
          guests.push(...pageData);
          currentPage += 1;
        } while (guests.length < totalCount);

        this.complaintGuests = guests;
      } catch (error) {
        if (isNoDataError(error)) {
          this.complaintGuests = [];
          return;
        }
        this.complaintGuests = [];
        this.handleApiError(error, 'Loading guests');
      } finally {
        this.isFetchingComplaintGuests = false;
      }
    },
    async fetchComplaints() {
      this.isLoading = true;
      try {
        const objectId = this.$route.params.id;
        const response = await apiService.getComplaints(objectId);

        if (response.data) {
          this.complaints = (response.data.data || []).map(complaint => this.normalizeComplainImages(complaint));
          this.totalComplaints = parseInt(response.data.totalcount) || 0;
          this.totalInProgress = parseInt(response.data.totalInProgress) || 0;
          this.totalPending = parseInt(response.data.totalPending) || 0;
          this.avgResolvedTime = response.data.avgResolvedTime || 0;
          
          this.filteredComplaints = [...this.complaints];
          this.totalPages = Math.ceil(this.complaints.length / this.pageSize);
        }
      } catch (error) {
        // Handle "no data" case - set empty array without showing error
        if (isNoDataError(error)) {
          this.complaints = [];
          this.filteredComplaints = [];
          this.totalPages = 0;
        } else if (isHmsSubscriptionInactiveError(error)) {
          return;
        } else {
          this.handleApiError(error, 'Loading complaints');
        }
      } finally {
        this.isLoading = false;
      }
    },
    async updateComplaintStatus(status) {
      if (!this.selectedComplaint) return;
      
      if (status === 'INPROGRESS' && !this.assignedStaff?.trim()) {
        this.showToast('Please enter a staff member name', 'error');
        return;
      }

      this.isUpdating = true;
      try {
        const objectId = this.$route.params.id;
        const complainId = this.selectedComplaint.complainId;
        const guestId = this.selectedComplaint.guestId;
        
        const payload = {
          status: status
        };
        
        if (this.assignedStaff?.trim()) {
          payload.assignedStaff = this.assignedStaff.trim();
        }
        if (this.staffMobileNumber?.trim()) {
          payload.staffMobileNumber = this.staffMobileNumber.trim();
        }

        await apiService.updateComplaint(complainId, objectId, guestId, payload);

        this.showToast('Complaint updated successfully', 'success');
        await this.fetchComplaints();
        this.closeComplaintDetails();
      } catch (error) {
        this.handleApiError(error, 'Updating complaint');
      } finally {
        this.isUpdating = false;
      }
    },
    handleSearch() {
      this.applyFilters();
    },
    handleFilterChange() {
      this.applyFilters();
    },
    applyFilters() {
      let filtered = [...this.complaints];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(complaint => 
          complaint.guestName?.toLowerCase().includes(query) ||
          complaint.complainName?.toLowerCase().includes(query) ||
          complaint.roomNumber?.toString().includes(query)
        );
      }

      // Priority filter
      if (this.selectedPriority) {
        filtered = filtered.filter(complaint => complaint.priority === this.selectedPriority);
      }

      // Status filter
      if (this.selectedStatus) {
        // Map filter values to API status values
        const statusMap = {
          'PENDING': 'OPEN',
          'INPROGRESS': 'INPROGRESS',
          'CLOSED': 'CLOSED'
        };
        const apiStatus = statusMap[this.selectedStatus] || this.selectedStatus;
        filtered = filtered.filter(complaint => complaint.status === apiStatus);
      }

      this.filteredComplaints = filtered;
      this.totalPages = Math.ceil(filtered.length / this.pageSize);
      this.currentPage = 1;
    },
    openComplaintDetails(complaint) {
      // Normalize complainImages to ensure it's always an array
      const normalizedComplaint = this.normalizeComplainImages({ ...complaint });
      this.selectedComplaint = normalizedComplaint;
      this.assignedStaff = complaint.assignedStaff || '';
      this.staffMobileNumber = complaint.staffMobileNumber || '';
      this.showDetailsModal = true;
      document.body.style.overflow = 'hidden';
    },
    closeComplaintDetails() {
      this.showDetailsModal = false;
      this.selectedComplaint = null;
      this.assignedStaff = '';
      this.staffMobileNumber = '';
      document.body.style.overflow = 'auto';
    },
    openImageModal(imageUrl) {
      this.selectedImageUrl = imageUrl;
      this.showImageModal = true;
      document.body.style.overflow = 'hidden';
    },
    closeImageModal() {
      this.showImageModal = false;
      this.selectedImageUrl = '';
      document.body.style.overflow = 'auto';
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        // Scroll to top of table
        const tableSection = document.querySelector('.table-section');
        if (tableSection) {
          tableSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },
    getAuthToken() {
      const token = readSessionToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }
      return token;
    },
    formatDate(dateString) {
      if (!dateString) return '--';
      try {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
      } catch (error) {
        return dateString;
      }
    },
    getPriorityClass(priority) {
      if (priority === 'HIGH') return 'priority-high';
      if (priority === 'MEDIUM') return 'priority-medium';
      if (priority === 'LOW') return 'priority-low';
      return 'priority-normal';
    },
    getPriorityLabel(priority) {
      if (priority === 'HIGH') return 'High';
      if (priority === 'MEDIUM') return 'Medium';
      if (priority === 'LOW') return 'Low';
      return 'Normal';
    },
    getStatusClass(status) {
      if (status === 'CLOSED') return 'status-done';
      if (status === 'INPROGRESS') return 'status-in-progress';
      if (status === 'OPEN') return 'status-pending';
      return 'status-pending';
    },
    getStatusLabel(status) {
      if (status === 'CLOSED') return 'Done';
      if (status === 'INPROGRESS') return 'In Progress';
      if (status === 'OPEN') return 'Pending';
      return 'Pending';
    },
    formatRoomType(roomType) {
      if (!roomType) return '--';
      // Convert "2S" to "2 Sharing", "3S" to "3 Sharing", etc.
      if (roomType.endsWith('S')) {
        const number = roomType.replace('S', '');
        return `${number} Sharing`;
      }
      return roomType;
    },
    getRaisedByLabel(raisedBy) {
      if (!raisedBy) return '--';
      // If raisedBy is 'GUEST' or 'Guest', return 'Guest'
      if (raisedBy === 'GUEST' || raisedBy === 'Guest' || raisedBy === 'guest') {
        return 'Guest';
      }
      // If raisedBy is 'ADMIN' or 'Admin', return 'Admin'
      if (raisedBy === 'ADMIN' || raisedBy === 'Admin' || raisedBy === 'admin') {
        return 'Admin';
      }
      // Default to Admin if it's not explicitly GUEST
      return 'Admin';
    },
    isVideoUrl(url) {
      return url && (url.includes('.mp4') || url.includes('.mov') || url.includes('video'));
    },
    showToast(message, type = 'error', duration = 5000) {
      const id = Date.now() + Math.random(); // Ensure unique ID
      this.toasts.push({ id, message, type });
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(id);
        }, duration);
      }
    },
    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    },
    // Enhanced API error handling
    handleApiError(error, context = 'Operation') {
      // Skip error handling for "no data" cases (status 400 with errorcode 2003)
      if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
        return;
      }

      console.error(`${context} error:`, error);
      
      let message = null;
      let type = 'error';

      if (error?.response?.data) {
        const errorData = error.response.data;
        const status = error.response.status;
        
        // For HTTP 400 errors, always use the reason field from API response
        if (status === 400) {
          if (errorData.reason) {
            message = errorData.reason;
          }
          // If no reason provided for 400 error, don't show toast (no generic messages)
        } else {
          // For other status codes, prioritize reason field
          if (errorData.reason) {
            message = errorData.reason;
          } else if (errorData.message) {
            message = errorData.message;
          } else {
            // Fallback messages for other status codes only if no reason/message
            switch (status) {
              case 401:
                message = 'Session expired. Please login again.';
                setTimeout(() => {
                  this.$router.push('/login');
                }, 2000);
                break;
              case 403:
                message = 'You do not have permission to perform this action.';
                break;
              case 404:
                message = 'The requested resource was not found.';
                break;
              case 429:
                message = 'Too many requests. Please wait a moment and try again.';
                break;
              case 500:
                message = 'Server error occurred. Please try again later.';
                break;
              case 503:
                message = 'Service temporarily unavailable. Please try again later.';
                break;
            }
          }
        }
      } else if (error?.request) {
        message = 'No response from server. Please check your internet connection.';
      } else if (error?.message) {
        message = error.message;
      }

      // Only show toast if we have a message (genuine errors only)
      if (message) {
        this.showToast(message, type);
      }
    },
    triggerImageUpload() {
      this.$refs.imageInput?.click();
    },
    async handleImageUpload(event) {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      for (let file of files) {
        // Check file size
        if (file.size > this.maxFileSize) {
          this.showToast('One or more images exceed 5MB limit. Please select smaller images.', 'error');
          continue;
        }

        // Check file type
        if (!this.allowedTypes.includes(file.type)) {
          this.showToast('Invalid file type. Please upload valid image files (JPEG, PNG, GIF, or WebP).', 'error');
          continue;
        }

        // Create preview
        const reader = new FileReader();
        const previewIndex = this.complaintImagePreviews.length;
        reader.onload = e => {
          this.complaintImagePreviews.push({
            url: e.target.result,
            uploading: true,
            file: file
          });
        };
        reader.readAsDataURL(file);

        // Upload image
        try {
          await this.uploadComplaintImage(file, previewIndex);
        } catch (error) {
          console.error('Image upload failed:', error);
          this.complaintImagePreviews.splice(previewIndex, 1);
        }
      }

      // Reset input
      event.target.value = '';
    },
    async uploadComplaintImage(file, index) {
      try {
        const formData = new FormData();
        formData.append('files', file);

        const uploadResponse = await apiService.uploadImage(formData);

        const uploadedFilenames = uploadResponse.data || [];
        if (uploadedFilenames.length > 0) {
          const imageFilename = uploadedFilenames[0];
          this.complaintImagePreviews[index].uploading = false;
          this.complaintImagePreviews[index].filename = imageFilename;
          this.newComplaint.complainImages.push(imageFilename);
          this.showToast('Image uploaded successfully!', 'success', 2000);
        } else {
          throw new Error('No image URL received from server');
        }
      } catch (error) {
        console.error('Image upload failed:', error);
        this.showToast('Failed to upload image. Please try again.', 'error');
        this.complaintImagePreviews.splice(index, 1);
        throw error;
      }
    },
    removeImage(index) {
      const removed = this.complaintImagePreviews.splice(index, 1)[0];
      if (removed.filename) {
        const filenameIndex = this.newComplaint.complainImages.indexOf(removed.filename);
        if (filenameIndex > -1) {
          this.newComplaint.complainImages.splice(filenameIndex, 1);
        }
      }
    },
    async createComplaint() {
      if (!this.newComplaint.guestId || !this.newComplaint.complainName || 
          !this.newComplaint.complainDescription || !this.newComplaint.priority) {
        this.showToast('Please fill in all required fields', 'error');
        return;
      }

      this.isCreating = true;
      try {
        // Ensure complainImages is always an array
        const complainImages = Array.isArray(this.newComplaint.complainImages) 
          ? this.newComplaint.complainImages 
          : (this.newComplaint.complainImages ? [this.newComplaint.complainImages] : []);
        
        const payload = {
          complainName: this.newComplaint.complainName,
          complainDescription: this.newComplaint.complainDescription,
          priority: this.newComplaint.priority,
          complainImages: complainImages
        };

        await apiService.createComplaint(this.newComplaint.guestId, payload);

        this.showToast('Complaint created successfully', 'success');
        this.closeCreateModal();
        await this.fetchComplaints();
      } catch (error) {
        this.handleApiError(error, 'Creating complaint');
      } finally {
        this.isCreating = false;
      }
    },
    async openCreateModal() {
      this.showCreateModal = true;
      document.body.style.overflow = 'hidden';
      await this.fetchComplaintGuests();
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.newComplaint = {
        guestId: '',
        complainName: '',
        complainDescription: '',
        priority: '',
        complainImages: []
      };
      this.complaintImagePreviews = [];
      document.body.style.overflow = 'auto';
    },
    openEditModal(complaint) {
      this.editingComplaint = complaint;
      
      // Normalize complainImages to ensure it's always an array
      const normalizedComplaint = this.normalizeComplainImages({ ...complaint });
      
      // Extract filenames from image URLs
      const extractFilename = (imageUrl) => {
        if (!imageUrl) return '';
        // If it's already just a filename, return it
        if (!imageUrl.includes('/') && !imageUrl.includes('http')) {
          return imageUrl;
        }
        // Extract filename from URL
        const parts = imageUrl.split('/');
        return parts[parts.length - 1];
      };
      
      const imageFilenames = (normalizedComplaint.complainImages || []).map(extractFilename);
      
      this.editComplaint = {
        complainId: normalizedComplaint.complainId,
        guestId: normalizedComplaint.guestId,
        complainName: normalizedComplaint.complainName || '',
        complainDescription: normalizedComplaint.complainDescription || '',
        priority: normalizedComplaint.priority || '',
        complainImages: imageFilenames
      };
      
      // Load existing images as previews
      this.editImagePreviews = (normalizedComplaint.complainImages || []).map((imageUrl) => {
        const fullUrl = getComplaintImageUrl(imageUrl);
        const filename = extractFilename(imageUrl);
        return {
          url: fullUrl,
          filename: filename,
          uploading: false
        };
      });
      
      this.showEditModal = true;
      document.body.style.overflow = 'hidden';
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editingComplaint = null;
      this.editComplaint = {
        complainId: '',
        guestId: '',
        complainName: '',
        complainDescription: '',
        priority: '',
        complainImages: []
      };
      this.editImagePreviews = [];
      document.body.style.overflow = 'auto';
    },
    triggerEditImageUpload() {
      this.$refs.editImageInput?.click();
    },
    async handleEditImageUpload(event) {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      for (let file of files) {
        // Check file size
        if (file.size > this.maxFileSize) {
          this.showToast('One or more images exceed 5MB limit. Please select smaller images.', 'error');
          continue;
        }

        // Check file type
        if (!this.allowedTypes.includes(file.type)) {
          this.showToast('Invalid file type. Please upload valid image files (JPEG, PNG, GIF, or WebP).', 'error');
          continue;
        }

        // Create preview
        const reader = new FileReader();
        const previewIndex = this.editImagePreviews.length;
        reader.onload = e => {
          this.editImagePreviews.push({
            url: e.target.result,
            uploading: true,
            file: file
          });
        };
        reader.readAsDataURL(file);

        // Upload image
        try {
          await this.uploadEditComplaintImage(file, previewIndex);
        } catch (error) {
          console.error('Image upload failed:', error);
          this.editImagePreviews.splice(previewIndex, 1);
        }
      }

      // Reset input
      event.target.value = '';
    },
    async uploadEditComplaintImage(file, index) {
      try {
        const formData = new FormData();
        formData.append('files', file);

        const uploadResponse = await apiService.uploadImage(formData);

        const uploadedFilenames = uploadResponse.data || [];
        if (uploadedFilenames.length > 0) {
          const imageFilename = uploadedFilenames[0];
          this.editImagePreviews[index].uploading = false;
          this.editImagePreviews[index].filename = imageFilename;
          this.editComplaint.complainImages.push(imageFilename);
          this.showToast('Image uploaded successfully!', 'success', 2000);
        } else {
          throw new Error('No image URL received from server');
        }
      } catch (error) {
        console.error('Image upload failed:', error);
        this.showToast('Failed to upload image. Please try again.', 'error');
        this.editImagePreviews.splice(index, 1);
        throw error;
      }
    },
    removeEditImage(index) {
      const removed = this.editImagePreviews.splice(index, 1)[0];
      if (removed.filename) {
        const filenameIndex = this.editComplaint.complainImages.indexOf(removed.filename);
        if (filenameIndex > -1) {
          this.editComplaint.complainImages.splice(filenameIndex, 1);
        }
      }
    },
    async updateComplaint() {
      if (!this.editComplaint.complainName || !this.editComplaint.complainDescription || !this.editComplaint.priority) {
        this.showToast('Please fill in all required fields', 'error');
        return;
      }

      this.isUpdatingComplaint = true;
      try {
        const objectId = this.$route.params.id;
        const complainId = this.editComplaint.complainId;
        const guestId = this.editComplaint.guestId;
        
        // Ensure we only send filenames, not full URLs
        const extractFilename = (imageUrl) => {
          if (!imageUrl) return '';
          // If it's already just a filename, return it
          if (!imageUrl.includes('/') && !imageUrl.includes('http')) {
            return imageUrl;
          }
          // Extract filename from URL
          const parts = imageUrl.split('/');
          return parts[parts.length - 1];
        };
        
        // Ensure complainImages is always an array before processing
        const complainImagesArray = Array.isArray(this.editComplaint.complainImages) 
          ? this.editComplaint.complainImages 
          : (this.editComplaint.complainImages ? [this.editComplaint.complainImages] : []);
        
        const imageFilenames = complainImagesArray.map(extractFilename);
        
        const payload = {
          complainName: this.editComplaint.complainName,
          complainDescription: this.editComplaint.complainDescription,
          priority: this.editComplaint.priority,
          complainImages: imageFilenames
        };

        await apiService.updateComplaint(complainId, objectId, guestId, payload);

        this.showToast('Complaint updated successfully', 'success');
        this.closeEditModal();
        await this.fetchComplaints();
      } catch (error) {
        this.handleApiError(error, 'Updating complaint');
      } finally {
        this.isUpdatingComplaint = false;
      }
    }
  },
  mounted() {
    this.fetchComplaints();
  }
};
</script>

<style scoped>
.maintenance-container {
  padding: 0;
  background-color: #f8fafc;
  min-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
}

/* Header Section */
.header-section {
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 0;
  border: none;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;
  margin-top: 20px;
}

.header-content {
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.025em;
}

.filter-select {
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #1a1a1a;
  cursor: pointer;
  outline: none;
  min-width: 150px;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.filter-select:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

/* Stats Section */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.stat-card {
  background: white;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  color: #64748b;
  flex-shrink: 0;
}

.stat-icon svg {
  width: 20px;
  height: 20px;
}

.stat-icon.active {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
}

.stat-content {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.stat-content h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px 0;
  line-height: 1.2;
}

.stat-content p {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 4px 0;
  font-weight: 500;
}

.stat-change {
  font-size: 12px;
  font-weight: 500;
}

.stat-change.positive {
  color: #10b981;
}

.stat-change.negative {
  color: #ef4444;
}

/* Filter Section */
.filter-section {
  background: white;
  border-radius: 0;
  padding: 16px 24px;
  margin-bottom: 0;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.search-box {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: #94a3b8;
  z-index: 1;
}

.search-box input {
  width: 100%;
  padding: 10px 16px 10px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
}

.search-box input:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.filter-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
}

/* Table Section */
.table-section {
  flex: 1;
  padding: 0;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-container {
  background: white;
  border-radius: 0;
  box-shadow: none;
  border: none;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  backdrop-filter: blur(2px);
  border-radius: 16px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #5b2e8e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.table-wrapper {
  overflow-x: auto;
  flex: 1;
}

.complaint-table {
  width: 100%;
  border-collapse: collapse;
}

.complaint-table thead {
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
}

.complaint-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  vertical-align: middle;
}

.complaint-table th:nth-child(6),
.complaint-table th:nth-child(7),
.complaint-table th:nth-child(10) {
  text-align: center;
}

.complaint-table td {
  padding: 12px 16px;
  font-size: 14px;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  text-align: left;
  vertical-align: middle;
}

.complaint-table td:nth-child(6),
.complaint-table td:nth-child(7),
.complaint-table td:nth-child(10) {
  text-align: center;
}

.complaint-table td:nth-child(6) .priority-badge,
.complaint-table td:nth-child(7) .status-badge {
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.complaint-table td:nth-child(10) .action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background: #f8fafc;
  transform: scale(1.001);
}

.empty-row {
  text-align: center;
}

.empty-message {
  padding: 48px 24px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #64748b;
}

.empty-state svg {
  color: #cbd5e1;
}

.empty-illustration-img {
  width: 250px;
  height: 250px;
  max-width: 100%;
  object-fit: contain;
  margin-bottom: 16px;
}

@media (max-width: 480px) {
  .empty-illustration-img {
    width: 180px;
    height: 180px;
  }
}

/* Badges */
.priority-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.priority-badge .priority-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

.priority-badge.priority-high {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #dc2626;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
}

.priority-badge.priority-medium {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #d97706;
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.2);
}

.priority-badge.priority-low {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.2);
}

.priority-badge.priority-normal {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #7c3aed;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.2);
}

.status-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.status-done {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.2);
}

.status-badge.status-in-progress {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #2563eb;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
}

.status-badge.status-pending {
  background: linear-gradient(135deg, #fed7aa, #fdba74);
  color: #ea580c;
  box-shadow: 0 2px 8px rgba(234, 88, 12, 0.2);
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
  width: 36px;
  height: 36px;
}

.action-btn:hover {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(91, 46, 142, 0.2);
}

.action-view:hover {
  color: #3b82f6;
}

.action-edit:hover {
  color: #5b2e8e;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  padding: 24px 32px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.pagination-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.pagination-btn:hover:not(:disabled) {
  border-color: #5b2e8e;
  color: #5b2e8e;
  background: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f8fafc;
}

.pagination-btn.active {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  border-color: #5b2e8e;
  box-shadow: 0 4px 14px rgba(91, 46, 142, 0.3);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.create-modal {
  max-width: 600px;
  overflow-x: hidden;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  position: relative;
  overflow: hidden;
  color: white;
  padding: 0;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  opacity: 0.95;
}

.header-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03' fill-rule='evenodd'%3E%3Cpath d='m0 40l40-40h-40v40zm40 0v-40h-40l40 40z'/%3E%3C/g%3E%3C/svg%3E") repeat;
}

.modal-header-content {
  position: relative;
  z-index: 2;
  padding: 42px;
  flex: 1;
  display: flex;
  align-items: center;
}

.modal-header h2 {
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 3;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  color: white;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.close-btn svg {
  width: 20px;
  height: 20px;
}

.modal-body {
  padding: 32px;
  padding-bottom: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  position: relative;
  max-width: 100%;
  box-sizing: border-box;
}

.complaint-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.complaint-title-section h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  flex: 1;
}

.priority-tag {
  padding: 8px 18px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.priority-tag.priority-high {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #dc2626;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
}

.priority-tag.priority-medium {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #d97706;
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.2);
}

.priority-tag.priority-low {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.2);
}

.priority-tag.priority-normal {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #7c3aed;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.2);
}

.details-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.details-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
}

.details-section h4 {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.detail-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.detail-item label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
  display: block;
}

.detail-item span {
  font-size: 15px;
  color: #1e293b;
  font-weight: 600;
}

.description-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reported-date {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.description-text {
  font-size: 14px;
  color: #1a1a1a;
  line-height: 1.6;
  margin: 0;
}

.attachments-section {
  margin-top: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.attachment-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e2e8f0;
}

.attachment-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
}

.play-overlay svg {
  width: 32px;
  height: 32px;
}

.staff-input-wrapper {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.edit-note-section {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  margin: 0;
  padding: 0;
}

.edit-note {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 1px solid #fbbf24;
  border-radius: 8px;
  color: #92400e;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.2);
  white-space: nowrap;
}

.edit-note svg {
  flex-shrink: 0;
  color: #d97706;
  width: 18px;
  height: 18px;
}

.staff-input {
  width: 100%;
  max-width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  background: white;
  color: #1e293b;
  outline: none;
  transition: all 0.2s ease;
  font-weight: 500;
  box-sizing: border-box;
}

.staff-input:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 4px rgba(91, 46, 142, 0.1);
  background: #fafbfc;
}

.staff-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
  color: #94a3b8;
  border-color: #e2e8f0;
}

.modal-actions {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  padding: 24px 32px;
  background: white;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  z-index: 10;
  flex-shrink: 0;
}

.btn-action {
  flex: 1;
  padding: 14px 28px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  min-height: 48px;
}

.btn-action::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn-action:hover:not(:disabled)::before {
  left: 100%;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-in-progress {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: white;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
}

.btn-in-progress:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
  background: linear-gradient(135deg, #1d4ed8, #2563eb);
}

.btn-resolved {
  background: linear-gradient(135deg, #10b981, #34d399);
  color: white;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
}

.btn-resolved:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
  background: linear-gradient(135deg, #059669, #10b981);
}

/* Image Modal */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
}

.image-modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.image-modal-content img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

.image-close-btn {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s;
}

.image-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Responsive Design */
/* Screens below 720px */
@media (max-width: 720px) {
  .maintenance-container {
    padding: 0;
  }

  .header-section {
    margin-top: 12px;
  }

  .header-content {
    flex-direction: column;
    gap: 14px;
    align-items: stretch;
    padding: 14px 16px;
  }

  .title-icon {
    width: 40px;
    height: 40px;
  }

  .title-icon svg {
    width: 20px;
    height: 20px;
  }

  .page-title {
    gap: 12px;
  }

  .page-title h1 {
    font-size: 18px;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .stats-section {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding: 14px 16px;
  }

  .stat-card {
    padding: 12px 14px;
  }

  .stat-content h3 {
    font-size: 16px;
  }

  .stat-content p {
    font-size: 12px;
  }

  .filter-section {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 14px 16px;
  }

  .filter-controls {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }

  .filter-select {
    width: 100%;
    padding: 10px 14px;
    font-size: 14px;
  }

  .edit-note-section {
    width: 100%;
    justify-content: flex-start;
    margin: 0;
  }

  .edit-note {
    white-space: normal;
    font-size: 12px;
    padding: 10px 12px;
    width: 100%;
    box-sizing: border-box;
  }

  .details-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .modal-actions {
    flex-direction: column;
    padding: 20px 16px;
  }

  .btn-action {
    width: 100%;
  }

  .complaint-table {
    font-size: 12px;
  }

  .complaint-table th,
  .complaint-table td {
    padding: 10px 6px;
    font-size: 12px;
  }

  .pagination-wrapper {
    padding: 16px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .pagination-btn {
    min-width: 36px;
    height: 36px;
    padding: 0 12px;
    font-size: 14px;
  }
}

/* Very small screens (below 480px) */
@media (max-width: 480px) {
  .header-content {
    padding: 12px 14px;
  }

  .title-icon {
    width: 36px;
    height: 36px;
  }

  .title-icon svg {
    width: 18px;
    height: 18px;
  }

  .page-title {
    gap: 10px;
  }

  .page-title h1 {
    font-size: 16px;
  }

  .stats-section {
    grid-template-columns: 1fr;
    padding: 12px 14px;
    gap: 8px;
  }

  .stat-card {
    padding: 10px 12px;
  }

  .stat-icon {
    width: 36px;
    height: 36px;
  }

  .stat-icon svg {
    width: 18px;
    height: 18px;
  }

  .stat-content h3 {
    font-size: 15px;
  }

  .stat-content p {
    font-size: 12px;
  }

  .filter-section {
    padding: 12px 14px;
    gap: 10px;
  }

  .filter-controls {
    gap: 8px;
  }

  .filter-select {
    padding: 9px 12px;
    font-size: 14px;
  }

  .edit-note {
    font-size: 12px;
    padding: 8px 10px;
  }

  .edit-note svg {
    width: 16px;
    height: 16px;
  }

  .complaint-table th,
  .complaint-table td {
    padding: 8px 4px;
    font-size: 12px;
  }

  .pagination-wrapper {
    padding: 12px;
    gap: 6px;
  }

  .pagination-btn {
    min-width: 32px;
    height: 32px;
    padding: 0 10px;
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .maintenance-container {
    padding: 0;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    padding: 16px 20px;
  }

  .stats-section {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 16px 20px;
  }

  .filter-section {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 16px 20px;
  }

  .filter-controls {
    flex-direction: row;
    width: 100%;
    gap: 12px;
  }

  .filter-select {
    flex: 1;
  }

  .edit-note-section {
    width: 100%;
    justify-content: flex-start;
  }

  .edit-note {
    white-space: normal;
    font-size: 12px;
    padding: 10px 12px;
    width: 100%;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-direction: column;
  }

  .complaint-table {
    font-size: 12px;
  }

  .complaint-table th,
  .complaint-table td {
    padding: 12px 8px;
  }
}

/* Toast Notification Styles */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-left: 4px solid transparent;
  min-width: 320px;
  pointer-events: auto;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toast-error {
  border-left-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.toast-success {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4, #ffffff);
}

.toast-info {
  border-left-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.toast-warning {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb, #ffffff);
}

.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.toast-error .toast-icon {
  color: #ef4444;
}

.toast-success .toast-icon {
  color: #10b981;
}

.toast-info .toast-icon {
  color: #3b82f6;
}

.toast-warning .toast-icon {
  color: #f59e0b;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-message {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  color: #6b7280;
  background: rgba(0, 0, 0, 0.05);
  transform: scale(1.1);
}

/* Toast Animations */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Responsive Toast Styles */
@media (max-width: 768px) {
  .toast-container {
    top: 16px;
    right: 16px;
    left: 16px;
    max-width: none;
  }

  .toast {
    min-width: auto;
    width: 100%;
    padding: 14px 16px;
  }

  .toast-message {
    font-size: 14px;
  }
}

/* Create Complaint Form Styles */
.create-complaint-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  display: block;
}

.required {
  color: #ef4444;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  max-width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  background: white;
  color: #1e293b;
  outline: none;
  transition: all 0.2s ease;
  font-family: inherit;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 4px rgba(91, 46, 142, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.form-select {
  cursor: pointer;
}

/* Image Upload Section */
.image-upload-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.image-upload-area {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8fafc;
}

.image-upload-area:hover {
  border-color: #5b2e8e;
  background: #f1f5f9;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #64748b;
}

.upload-placeholder svg {
  color: #94a3b8;
}

.upload-placeholder p {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #475569;
}

.upload-hint {
  font-size: 12px;
  color: #94a3b8;
}

.image-previews {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.image-preview-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.image-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 10;
  animation: fadeInOverlay 0.3s ease-out;
}

.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.modern-spinner {
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
  width: 56px;
  height: 56px;
  border-top-color: #ffffff;
  border-right-color: rgba(255, 255, 255, 0.3);
  animation-duration: 1.2s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
}

.spinner-ring:nth-child(2) {
  width: 44px;
  height: 44px;
  border-top-color: #a78bfa;
  border-right-color: rgba(167, 139, 250, 0.3);
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  animation-direction: reverse;
  top: 6px;
  left: 6px;
}

.spinner-ring:nth-child(3) {
  width: 32px;
  height: 32px;
  border-top-color: #7c3aed;
  border-right-color: rgba(124, 58, 237, 0.3);
  animation-duration: 0.8s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  top: 12px;
  left: 12px;
}

@keyframes spin-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeInOverlay {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.uploading-text {
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  animation: pulse-text 2s ease-in-out infinite;
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.remove-image-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(239, 68, 68, 0.9);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s;
  padding: 0;
}

.remove-image-btn:hover {
  background: rgba(239, 68, 68, 1);
  transform: scale(1.1);
}

.remove-image-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-actions {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 24px 32px;
  background: white;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  z-index: 10;
  flex-shrink: 0;
  margin: 0;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}

.btn-primary {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  box-shadow: 0 4px 14px rgba(91, 46, 142, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(91, 46, 142, 0.4);
  background: linear-gradient(135deg, #4c1d75, #6b21d4);
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.create-modal {
  max-width: 600px;
}
</style>
